import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useCrewWriteMutation } from '../../hooks/useCrewWriteMutation';
import { CREW_PHASES, CREW_PHASE_LABEL, phaseOf, type CrewPhaseValue } from '../../lib/dispatch-crew-phase';
import type { CrewPhase, DispatchJob } from '../../types/dispatch';
import { hasWashDate } from '../../lib/job-phase-dates';

const memberName = (m: { first_name?: string | null; last_name?: string | null }) =>
  [m.first_name, m.last_name].filter((n) => n && n.trim()).join(' ') || '—';

/**
 * "Crew phase" from a card's menu: every hand on the job, and which day each
 * works. The whole crew, not the card's shown subset — this is where a hand
 * who is on the wash only can be moved back onto the run.
 *
 * A hand with no phase stated shows Both selected and says it is not set:
 * NULL and 'both' behave the same on the board and on the call sheet, and the
 * note keeps the screen from claiming a value nobody wrote. Save sends only
 * the hands whose selection changed, as `phases`, which sets a phase without
 * re-adding the hand (and so without re-notifying them).
 *
 * Portalled to the body, and every pointer and click event stopped at the
 * portal's root: the card that opens this is a drag source whose body
 * navigates on click, and React bubbles portal events through the component
 * tree, not the DOM one.
 */
export const CrewPhaseDialog: React.FC<{ job: DispatchJob; onClose: () => void }> = ({ job, onClose }) => {
  const crewWrite = useCrewWriteMutation();
  const [draft, setDraft] = useState<Record<string, CrewPhaseValue>>(() =>
    Object.fromEntries(job.crew.map((m) => [m.id, phaseOf(m)])),
  );

  const changes = job.crew
    .filter((m) => draft[m.id] !== phaseOf(m))
    .map((m) => ({ userId: m.id, phase: draft[m.id] }));

  const washDated = hasWashDate(job);

  const save = () => {
    // Never a phase on a job with no wash date — there is only the run.
    if (!washDated || changes.length === 0) {
      onClose();
      return;
    }
    crewWrite.mutate(
      { jobId: job.id, body: { phases: changes } },
      {
        onSuccess: () => {
          toast.success(`Crew phases saved on ${job.job_number}`);
          onClose();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, `Could not save crew phases on ${job.job_number}.`)),
      },
    );
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return createPortal(
    <div onPointerDown={stop} onTouchStart={stop} onClick={stop} onMouseDown={stop}>
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <div role="dialog" aria-label={`Crew phase — ${job.job_number}`}>
          <DialogHeader>
            <DialogTitle>Crew phase — {job.job_number}</DialogTitle>
            <p className="text-xs text-fieldiq-muted mt-1">Which day each hand works. Their call sheet follows this.</p>
          </DialogHeader>
          {/* No wash date → one phase, the run: nothing to pick and nothing
              is saved. A same-day wash HAS a date and keeps the picker — the
              wash crew often goes out first. */}
          {!washDated ? (
            <p className="text-sm text-fieldiq-muted" data-testid="crew-phase-no-wash">
              This job has no wash date, so every hand works the run. Add a wash date to split the crew.
            </p>
          ) : job.crew.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No crew on this job.</p>
          ) : (
            <ul className="space-y-2">
              {job.crew.map((m) => {
                const value = draft[m.id] ?? null;
                const shown: CrewPhase = value ?? 'both';
                return (
                  <li key={m.id} className="flex items-center justify-between gap-3" aria-label={memberName(m)}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{memberName(m)}</p>
                      {value === null && <p className="text-[11px] text-fieldiq-muted">Not set — every phase</p>}
                    </div>
                    <div className="flex flex-shrink-0 rounded-lg bg-fieldiq-dark p-0.5" role="group" aria-label={`${memberName(m)} phase`}>
                      {CREW_PHASES.map((p) => (
                        <button
                          key={p}
                          type="button"
                          aria-pressed={shown === p}
                          onClick={() => setDraft((d) => ({ ...d, [m.id]: p }))}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                            shown === p ? 'bg-fieldiq-accent text-white' : 'text-fieldiq-text hover:text-white'
                          }`}
                        >
                          {CREW_PHASE_LABEL[p]}
                        </button>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-fieldiq-card border border-fieldiq-border text-white"
            >
              Cancel
            </button>
            {washDated && <button
              type="button"
              onClick={save}
              disabled={crewWrite.isPending}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-fieldiq-accent text-white hover:bg-amber-600 disabled:opacity-50"
            >
              Save
            </button>}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
    </div>,
    document.body,
  );
};
